import { Box, useMediaQuery, useTheme } from '@mui/material';
import historySummaryBg from '@/assets/imgs/home/history_summary_bg_v2.png';
import brain from '@/assets/imgs/home/brain.png';
import portalLogo from '@/assets/portal_logo.png';

import maskBg from '@/assets/imgs/home/mask_group.png';
import { styled } from '@mui/material/styles';
import HistorySection, { HistoryData } from '@/components/history/HistorySection';
import HistoryLayout from '@/components/history/HistoryLayout';
import { Title, TitleBold, Sub, SubBold } from '@/components/common/TextStyle';

const Root = styled(Box)({
  position: 'relative',
  width: '100vw',
  height: '100vh',
  overflow: 'hidden',
});

const GradientBackground = styled(Box)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  background: 'linear-gradient(180deg, #262730 0%, #213599 100%)',
  zIndex: -4,
});

const ImageBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100vw',
  height: '100vh',
  backgroundImage: `url(${historySummaryBg})`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  zIndex: -3,
  [theme.breakpoints.down('sm')]: {
    backgroundPosition: '15% center',
  },
}));

const BlurCircle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '34.1vw',
  height: '60.6vh',
  backgroundColor: '#5B93D81A', // #5B93D8 10% 투명도
  filter: 'blur(100px)',
  borderRadius: '50%',
  pointerEvents: 'none', // 선택 안되게 (선택 사항)
  zIndex: -2,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MaskBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '69.5vw',
  top: '10.9',
  width: '34.1vw',
  height: '115.4vh',
  backgroundImage: `url(${maskBg})`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  zIndex: -2,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MainLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  paddingTop: '80px',
  height: '100vh',
  paddingLeft: '5.2vw',
  paddingRight: '5.2vw',
  paddingBottom: '5.2vw',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    paddingTop: '15px',
    paddingLeft: '3.5vw',
    paddingRight: '3.5vw',
    paddingBottom: '3.5vw',
    // gap: '2.0vh',
  },
}));

const HistoryBackground = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: 0, // 원하는 여백
  bottom: 0,
  width: '629px',
  height: '429px',
  backgroundImage: `url(${brain})`,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right bottom',
  zIndex: -1,
  pointerEvents: 'none', // 클릭 막기
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const operationHistory: HistoryData[] = [
  { year: '2025 ~ 2026', description: '25~26년도 공공데이터포털 및 서비스 위탁운영' },
  { year: '2023 ~ 2024', description: '2023년~2024년 공공데이터포털 및 서비스 위탁운영' },
  { year: '2021 ~ 2022', description: '공공데이터포털 운영 및 유지관리(’21~’22)' },
  { year: '2020', description: '공공데이터포털 및 범정부 데이터 플랫폼 통합 운영·유지보수 사업' },
  { year: '2019', description: '2019년도 공공데이터포털(data.go.kr) 위탁 운영 및 서비스 개선 사업' },
  {
    year: '2016 ~ 2017',
    description: '2016~2017년도 공공데이터포털(data.go.kr) 위탁운영 및 서비스 운영 개선 사업 서비스 기능개선',
  },
];

const improvementHistory: HistoryData[] = [
  { year: '2025 ~ 2026', description: '25~26년도 공공데이터포털 및 서비스 위탁운영' },
  { year: '2023 ~ 2024', description: '2023년~2024년 공공데이터포털 및 서비스 위탁운영' },
  { year: '2021 ~ 2022', description: '공공데이터포털 운영 및 유지관리(’21~’22)' },
  { year: '2022', description: '수요자 중심의 공공데이터 개방활용 체계 및 제공 기반 개선 사업 (2차)' },
  { year: '2021', description: '수요자 중심의 공공데이터 개방활용 체계 및 제공 기반 개선 사업' },
  { year: '2020', description: '공공데이터포털 및 범정부 데이터 플랫폼 통합 운영·유지보수 사업 데이터SOS 체계 및 Open API 통합 서비스 제공 기반 구축 개방형 응용프로그래밍 인터페이스 연계 및 관리체계 개선' },
  { year: '2019', description: '2019년도 공공데이터포털(data.go.kr) 위탁 운영 및 서비스 개선 사업' },
  {
    year: '2016 ~ 2017',
    description: '2016~2017년도 공공데이터포털(data.go.kr) 위탁운영 및 서비스 운영 개선 사업 서비스 기능개선',
  },
];

export default function ResponsiveLayout() {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <Root>
      <GradientBackground />
      <ImageBackground />
      <BlurCircle sx={{ top: '60.83vh', left: '-6.41vw' }} />
      <BlurCircle sx={{ top: '-10.3vh', left: '74.3vw' }} />
      <BlurCircle sx={{ width: '18vw', height: '32.1vh', top: '9.9vh', left: '32.1vw' }} />
      <MaskBackground />
      <MainLayout>
        <Box sx={{ flex: { xs: 0.8, sm: 1 }, alignContent: 'center' }}>
          <Box
            component="img"
            src={portalLogo}
            sx={{
              paddingBottom: { xs: '2vh', sm: '3vh' },
              width: { xs: '45vw', sm: 'auto' }, // sm 이하에서는 70%, 이상에서는 100%
              height: 'auto', // 비율 유지
            }}
          />
          <Box>
            <Title sx={{ paddingBottom: '3vh' }}>
              <TitleBold sx={{ paddingBottom: '0.5vh', letterSpacing: '-1.2px' }}>공공데이터포털 운영,</TitleBold>
              <Title>
                프람트테크놀로지는
                <br />
                대한민국 공공데이터 개방을{isSmUp ? ' ' : <br />}선도합니다.
              </Title>
            </Title>
            <Sub>
              <SubBold>2016</SubBold>년부터 공공데이터포털
              <SubBold> 운영·개선을 통해{isSmUp ? ' ' : <br />}고품질의 서비스</SubBold>를<SubBold> 제공</SubBold>하고
              있습니다.
              <br />
              시간이 증명한 <SubBold>전문성</SubBold>과<SubBold> 기술력</SubBold>으로
              <SubBold>{isSmUp ? ' ' : <br />}공공데이터 서비스</SubBold>를 완성합니다.
            </Sub>
          </Box>
        </Box>
        <Box
          sx={{
            flex: { xs: 1.2, sm: 1 },
            height: { xs: '50vh', sm: '100%' }, // 모바일에서 50vh로 제한
    maxHeight: { xs: '50vh', sm: '100%' }, // 최대 높이 명시
            pl: { xs: 0, sm: '1vw' },
            pt: { xs: 0, sm: '2vh' },
              paddingBottom: { xs: '10vh', sm: 0 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <HistoryLayout>
            <HistoryBackground />
            <HistorySection title="운영사업 연혁" items={operationHistory} />
            <HistorySection title="구축 및 개선 사업 연혁" items={improvementHistory} />
          </HistoryLayout>
        </Box>
      </MainLayout>
    </Root>
  );
}
